<template lang="pug">
  .width-100.m-top-5.m-bottom-8
    form.column(@submit.prevent='onSubmit')
      .warning.row.middle.m-bottom-2(v-if='showWarning')
        simple-svg.white-icon.m-bottom-auto.m-right-1(:filepath='infoIcon' width='18px' height='18px')
        p.text-small.white {{ $t("register.repeatedUserWarning") }}
        button.m-left-2.m-bottom-auto(@click='hideWarning')
          simple-svg.white-icon(:filepath='crossIcon' width='18px' height='18px')
      h1.title.bold.m-bottom-4.title-medium.blue {{ $t('register.title') }}
      h1(v-if='!isSchoolChange').bold.m-bottom-1.text-xbig
        | {{ $t('register.titleNewUser') }}
      h1(v-else).bold.m-bottom-1.text-xbig
        | {{ $t('register.titleExistingUser') }}
      h2(v-if='!isSchoolChange').text-big.dark-gray.m-bottom-3
        | {{ $t('trial.subTitle') }}
      .row.external-buttons.middle.center.m-bottom-3(v-if='!withGoogle && !withMicrosoft')
        a.external-button.secondary-button.row(type='button' :href='googleLink')
          simple-svg.m-right-1(:filepath='googleGIcon' width='18px' height='18px')
          | {{ $t('register.continueWithGoogle') }}
        button.external-button.secondary-button.row(type='button' @click='showMicrosoftPopup')
          simple-svg.m-right-1(:filepath='microsoftIcon' width='18px' height='18px')
          | {{ $t('register.continueWithMicrosoft') }}
      .divider.relative.width-100.m-bottom-3(v-if='!withGoogle && !withMicrosoft')
        span.absolute.text-normal.o O
      .column.input-container
        h1.text-xbig.bold.blue.m-bottom-1 {{ $t('register.schoolInformation') }}
        .form-group.m-bottom-2
          label.text-normal.m2-bottom-2
            | {{ $t("register.schoolNameLabel") }}
          input.input-border.text-normal(
            type='text'
            name='name'
            :value='trialUser.school.name'
            :placeholder='$t("register.schoolNamePlaceholder")'
            @input='updateSchoolValue'
            :class='{ error: schoolNameError, disabled: trialInvitation, filled: trialUser.school.name }'
            :disabled='trialInvitation'
          )
          .row.middle.m-top-1(v-if='schoolNameError')
            simple-svg.icon.red-icon.m4-right-1(:filepath='stopIcon' width='15px' height='15px')
            span.text-small.red
              | {{ $t('register.schoolNameError') }}
        .double-inputs-container.row
          .form-group.m-bottom-2
            label(v-show='!isSchoolChange').text-normal.m2-bottom-2
              | {{ $t("trial.schoolAddress") }}
            vue-google-autocomplete.input-border.text-normal(
              ref="schoolAddress"
              id="schoolAddress"
              :placeholder='trialInvitation ? trialUser.school.address : $t("trial.schoolAddressPlaceholder")'
              @placechanged='updateSchoolAddress'
              @blur='blurAddress'
              :class='{ error: schoolAddressError, disabled: trialInvitation, filled: trialUser.school.address }'
              v-show='!isSchoolChange'
              :disabled='trialInvitation'
            )
            .row.middle.m-top-1(v-if='schoolAddressError')
              simple-svg.icon.red-icon.m4-right-1(:filepath='stopIcon' width='15px' height='15px')
              span.text-small.red
                | {{ addressMessage }}
          .form-group.m-bottom-2(v-show='!isSchoolChange')
            label.text-normal.m4-bottom-2
              | {{ $t("register.educationalLevelsLabel") }}
            tag-input(
              :placeholder='$t("register.educationalLevelsPlaceholder")'
              :value='trialUser.school.impartedLevels'
              :options='levelsOptions'
              @change='updateSchoolLevels'
              keyName='value'
              labelName='label'
              name='impartedLevels'
              filled
              :disabled='trialInvitation'
              :error='impartedLevelsError'
            )
            .row.middle.m-top-1(v-if='impartedLevelsError')
              simple-svg.icon.red-icon.m4-right-1(:filepath='stopIcon' width='15px' height='15px')
              span.text-small.red
                | {{ $t('register.educationalLevelsError') }}
        h1.text-xbig.bold.blue.m-bottom-1 {{ $t('register.personalInformation') }}
        .form-group.m-bottom-2(v-if='!withGoogle && !withMicrosoft')
          label.text-normal.m2-bottom-2
            | {{ $t('register.nameAndSurnameLabel') }}
          input.input-border.text-normal(
            type='text'
            name='fullName'
            :value='trialUser.fullName'
            @input='updateValue'
            :placeholder='$t("register.nameAndSurnamePlaceholder")'
            :class='{ error: fullNameError, disabled: isSchoolChange, filled: trialUser.fullName }'
            :disabled='isSchoolChange'
          )
          .row.middle.m-top-1(v-if='fullNameError')
            simple-svg.icon.red-icon.m4-right-1(:filepath='stopIcon' width='15px' height='15px')
            span.text-small.red
              | {{ $t('register.nameAndSurnameError') }}
        .double-inputs-container.row
          .form-group(v-if='withGoogle || withMicrosoft')
            label.text-normal.m2-bottom-2
              | {{ $t('register.nameAndSurnameLabel') }}
            input.input-border.text-normal(
              type='fullName' name='fullName' disabled
              :value='trialUser.fullName'
              @input='updateValue'
              :class='{ filled: trialUser.fullName }'
            )
          .form-group(v-if='withGoogle || withMicrosoft')
            label.text-normal.m2-bottom-2
              | {{ $t('register.emailLabel') }}
            input.input-border.text-normal(
              type='email' name='email' disabled
              :value='trialUser.email'
              @input='updateValue'
              :class='{ filled: trialUser.email }'
            )
          .form-group
            label.text-normal.m4-bottom-2
              | {{ $t('register.chargeLabel') }}
            k-select(
              name='position'
              :options='getUserPosition'
              @selectOption='updatePosition'
              :preselectedOption='null'
              :placeholder='$t("register.chargePlaceholder")'
              keyName='position'
              valueName='position'
              withBorder
              :error='positionError'
              filled
            )
            .row.middle.m-top-1(v-show='positionError')
              simple-svg.red-icon.m4-right-1(:filepath='stopIcon' :width='"16px"' :height='"16px"')
              span.text-small.red
                | {{ $t('errors.positionRequiredError') }}
          .form-group
            .row
              label.text-normal.m4-bottom-2
                | {{ $t('register.phoneLabel') }}
              label.text-normal.dark-gray.m4-left-1
                | {{ $t('register.optional') }}
            telephone-input(
              name='cellPhoneNumber'
              :value='trialUser.cellPhoneNumber'
              @change='updateValueArguments'
              filled
            )
          .form-group(v-if='!withGoogle && !withMicrosoft')
            label.text-normal.m2-bottom-2
              | {{ $t('register.emailPlaceholder') }}
            input.input-border.text-normal(
              type='email'
              name='email'
              :disabled='trialInvitation'
              :value='trialUser.email'
              @input='updateValue'
              :placeholder='$t("register.emailPlaceholder")'
              :class='{ error: emailError, filled: trialUser.email }'
            )
            .row.middle.m-top-1(v-if='emailError')
              simple-svg.icon.red-icon.m4-right-1(:filepath='stopIcon' width='15px' height='15px')
              span.text-small.red
                | {{ emailMessage }}
          .form-group(v-if='!withGoogle && !withMicrosoft')
            label.text-normal.m2-bottom-2
              | {{ $t('register.repeatEmailLabel') }}
            input.input-border.text-normal(
              type='email'
              name='repeatEmail'
              :disabled='trialInvitation'
              :value='trialUser.repeatEmail'
              @input='updateValue'
              :placeholder='$t("register.repeatEmailPlaceholder")'
              :class='{ error: repeatEmailError, filled: trialUser.repeatEmail }'
            )
            .row.middle.m-top-1(v-if='repeatEmailError')
              simple-svg.icon.red-icon.m4-right-1(:filepath='stopIcon' width='15px' height='15px')
              span.text-small.red
                | {{ repeatEmailMessage }}
          .form-group(v-if='!withGoogle && !withMicrosoft')
            label.text-normal.m2-bottom-2
              | {{ $t("register.passwordLabel") }}
            password-validator(
              :value='trialUser.password'
              @updateValue='updatePasswordValue'
              :showErrors='showErrors'
              :validations='$v.trialUser.password'
              filled
            )
          .form-group(v-if='!withGoogle && !withMicrosoft')
            label.text-normal.m2-bottom-2
              | {{ $t('register.replacePasswordLabel') }}
            hidden-form-input(
              type='text'
              name='confirmPassword'
              :value='trialUser.confirmPassword'
              @input='updatePasswordValue'
              :placeholder='$t("register.replacePasswordPlaceholder")'
              :inputError='replacePasswordError'
              filled
            )
            .row.middle.m-top-1(v-if='replacePasswordError')
              simple-svg.icon.red-icon.m4-right-1(:filepath='stopIcon' width='15px' height='15px')
              span.text-small.red
                | {{ confirmPasswordMessage }}
      h1(v-if='isSchoolChange').bold.m4-bottom-4.text-xbig
        | {{ $t('register.enterPasswordExistingUser') }}
      .row.m-bottom-1
        label.row.middle
          input.checkbox.absolute(
            type='checkbox'
            name='mailContact'
            v-model='mailContact'
          )
          span.checkmark.m-right-08
          p.text-smal.m4-right-1
            | {{ $t('trial.mailContact') }}
      .column
        .row.m-bottom-4(:class='{ error: showTermsAndConditionsError }')
          label.row.middle
            input.checkbox.absolute(
              type='checkbox'
              name='termsAndConditions'
              v-model='termsAndConditions'
            )
            span.checkmark.m4-right-2(:class='{ "term-error": showTermsAndConditionsError }')
            p.text-smal.m4-right-1
              | {{ $t('register.accept') }}
          button.button-simple.lowercase(type='button' @click='handleTermsAndConditions')
            | {{ $t('register.termsAndConditions') }}
        .row.middle.m-bottom-1(v-if='showTermsAndConditionsError')
          simple-svg.icon.red-icon.m4-right-1(:filepath='stopIcon' :width='"15px"' :height='"15px"')
          span.text-small.red
            | {{ $t('register.termsAndConditionsError') }}
      button.primary-button.submit-button(type='submit' :disabled='!termsAndConditions')
        | {{ $t('register.button') }}
      .row.middle.m-top-1(v-if='generalError')
        simple-svg.icon.red-icon.m4-right-1(:filepath='stopIcon' :width='"15px"' :height='"15px"')
        span.text-small.red
          | {{ trialError }}
</template>

<script>
import { required, sameAs, email, minLength } from 'vuelidate/lib/validators'
import { mapActions, mapState } from 'vuex'
import amplitude from 'amplitude-js'

import HiddenFormInput from '@/components/HiddenFormInput'
import PasswordValidator from '@/components/Register/PasswordValidator'
import TelephoneInput from '@/components/Inputs/TelephoneInput'
import KSelect from '@/components/KSelect'
import VueGoogleAutocomplete from 'vue-google-autocomplete'
import TagInput from '@/components/Inputs/TagInput'

import stopIcon from '@/assets/ic_stop.svg'
import googleGIcon from '@/assets/ic_google_g.svg'
import microsoftIcon from '@/assets/ic_microsoft.svg'
import infoIcon from '@/assets/ic_info.svg'
import crossIcon from '@/assets/ic_cross.svg'

import { isUnique } from '../utils/validators.js'
import { toLowerCamelCase } from '../utils/string'
import { validatorAsyncFields, baseNewUserWithoutSchool } from '../utils/constants'
import { deepCopy } from '../utils/object'

import { usedMail } from '../services/UserService'
import { usedAddress } from '../services/SchoolService'
import { MSAL } from '@/config/msalConfig'
import { i18n } from '../main.js'
import { routes } from '../router.js'
import { getGoogleAccessToken } from '../utils/url.js'

export default {
  name: 'Trial',
  components: { HiddenFormInput, PasswordValidator, TelephoneInput, KSelect, VueGoogleAutocomplete, TagInput },
  data() {
    return {
      microsoftIcon,
      infoIcon,
      crossIcon,
      googleGIcon,
      showErrors: false,
      showWarning: true,
      termsAndConditions: false,
      mailContact: false,
      stopIcon,
      backendOffline: false,
      trialInvitation: false,
      isSchoolChange: false,
      formatedAddress: ''
    }
  },
  computed: {
    ...mapState({
      trialUser: state => state.trial.trialUser || {},
      positions: state => state.profile.userPositions || [],
      trialError: state => state.trial.trialError,
      showTrialInviteModal: state => state.behavior.trialInviteModal,
      educationalLevels: state => state.profile.educationalLevels,
      googleUserInfo: state => state.userInfo.googleUserInfo,
      microsoftUserInfo: state => state.userInfo.microsoftUserInfo
    }),
    withGoogle() {
      return Boolean(this.googleUserInfo)
    },
    withMicrosoft() {
      return Boolean(this.microsoftUserInfo)
    },
    googleLink() {
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID
      const redirectUri = `${window.location.origin}/trial`
      const responseType = 'token'
      const scope = 'email profile'
      return `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}`
    },
    levelsOptions() {
      return this.educationalLevels.map(level => ({
        ...level,
        value: level.id,
        label: i18n.t(`educationalLevels.${level.name.toLowerCase()}`)
      }))
    },
    getUserPosition() {
      return this.positions
        .filter(pos => pos.positionId)
        .map(level => ({ ...level, label: this.$t(`profile.position.${toLowerCamelCase(level.position)}`) }))
        .sort((a, b) => a.label.localeCompare(b.label))
    },
    fullNameError() {
      return this.showErrors && this.$v.trialUser.fullName.$invalid
    },
    emailError() {
      return this.showErrors && !this.$v.trialUser.email.$pending && this.$v.trialUser.email.$invalid
    },
    repeatEmailError() {
      return this.showErrors && this.$v.trialUser.repeatEmail.$invalid
    },
    passwordError() {
      return this.showErrors && this.$v.trialUser.password.$invalid
    },
    replacePasswordError() {
      return this.showErrors && this.$v.trialUser.confirmPassword.$invalid
    },
    cellPhoneNumberError() {
      return this.showErrors && this.$v.trialUser.cellPhoneNumber.$invalid
    },
    schoolNameError() {
      return this.showErrors && this.$v.trialUser.school.name.$invalid
    },
    schoolAddressError() {
      return this.showErrors && !this.$v.trialUser.school.address.$pending && this.$v.trialUser.school.address.$invalid
    },
    positionError() {
      return this.showErrors && !this.trialUser.position
    },
    showTermsAndConditionsError() {
      return this.showErrors && !this.termsAndConditions
    },
    generalError() {
      return this.showErrors && this.trialError
    },
    impartedLevelsError() {
      return this.showErrors && this.$v.trialUser.school.impartedLevels && this.$v.trialUser.school.impartedLevels.$invalid
    },
    emailMessage() {
      const { email, required } = this.$v.trialUser.email
      return !email || !required ? this.$t('register.emailError') : this.$t('register.duplicatedMail')
    },
    repeatEmailMessage() {
      const { required } = this.$v.trialUser.repeatEmail
      return !required ? this.$t('register.repeatEmailError') : this.$t('register.repeatEmailDifferentError')
    },
    confirmPasswordMessage() {
      const { required } = this.$v.trialUser.confirmPassword
      return !required ? this.$t('register.replacePasswordError') : this.$t('register.replacePasswordDifferentError')
    },
    addressMessage() {
      const { unique } = this.$v.trialUser.school.address
      return !unique ? this.$t('register.duplicatedAddres') : this.$t('register.schoolAddressError')
    }
  },
  watch: {
    withGoogle() {
      if (this.withGoogle) this.setGoogleData()
    },
    withMicrosoft() {
      if (this.withMicrosoft) this.setMicrosoftData()
    }
  },
  async created() {
    await MSAL.initialize()
    if (this.$route.hash || this.withGoogle) this.setGoogleData()
    if (this.withMicrosoft) this.setMicrosoftData()
    const inviteToken = this.$route.query.inviteToken
    inviteToken && await this.verifyInvitation(inviteToken) // llama GetInvitedUserInfozz
    this.hydrateNewUserPositions('trial') // GetPositionsForNewUser
    this.hydrateEducationalLevels()
  },
  beforeDestroy() {
    this.setNewTrialUser()
    this.clearGoogleUserInfo()
    this.clearMicrosoftUserInfo()
  },
  methods: {
    ...mapActions([
      'hydrateNewUser',
      'showElement',
      'hideElement',
      'hydrateSubjectsNewUser',
      'hydrateEducationalLevels',
      'hydrateNewUserPositions',
      'submitTrialUser',
      'changeTrialUserField',
      'login',
      'getLoggedUserInfo',
      'changeTrialSchoolValue',
      'setError',
      'setNewTrialUser',
      'getUserTrialInfoByInvitation',
      'submitTrialUserInvitation',
      'submitExternalTrialUser',
      'getExternalGoogleInfo',
      'getExternalMicrosoftInfo',
      'clearGoogleUserInfo',
      'clearMicrosoftUserInfo'
    ]),
    hideWarning() {
      this.showWarning = false
    },
    handleTermsAndConditions() {
      this.showElement({ element: 'termsAndConditionsModal' })
    },
    updateValue(e) {
      this.changeTrialUserField({ field: e.target.name, value: e.target.value })
    },
    updatePasswordValue(value, field) {
      this.changeTrialUserField({ field, value })
    },
    updatePosition(field, value) {
      this.changeTrialUserField({ field, value })
    },
    updateValueArguments(value, field) {
      this.changeTrialUserField({ field, value })
    },
    updateSchoolValue(e) {
      this.changeTrialSchoolValue({ field: e.target.name, value: e.target.value })
    },
    updateSchoolLevels(value, field) {
      this.changeTrialSchoolValue({ field, value })
    },
    showMicrosoftPopup() {
      MSAL.loginPopup({ scopes: ['User.Read'] })
        .then(async response => {
          if (response) this.setMicrosoftData(response.accessToken)
        })
        .catch((error) => {
          console.error(error)
        })
    },
    async setMicrosoftData(token) {
      this.showElement({ element: 'fullPageLoader' })
      if (token) await this.getExternalMicrosoftInfo(token)
      this.changeTrialUserField({ field: 'fullName', value: this.microsoftUserInfo.displayName })
      this.changeTrialUserField({ field: 'email', value: this.microsoftUserInfo.mail })
      this.changeTrialUserField({ field: 'repeatEmail', value: this.microsoftUserInfo.mail })
      this.hideElement({ element: 'fullPageLoader' })
      this.withMicrosoft = true
    },
    async setGoogleData() {
      this.showElement({ element: 'fullPageLoader' })
      if (this.$route.hash) {
        const token = getGoogleAccessToken(this.$route)
        await this.getExternalGoogleInfo(token)
      }
      this.changeTrialUserField({ field: 'fullName', value: this.googleUserInfo.name })
      this.changeTrialUserField({ field: 'email', value: this.googleUserInfo.email })
      this.changeTrialUserField({ field: 'repeatEmail', value: this.googleUserInfo.email })
      this.hideElement({ element: 'fullPageLoader' })
      this.$router.push({ name: routes.trial })
    },
    async onSubmit() {
      this.showErrors = true
      const isValid = this.$v.trialUser.$pending ? await this.waitForValidation() : !this.$v.trialUser.$invalid
      if (this.termsAndConditions && isValid && !this.positionError) {
        this.setError()
        if (this.withGoogle || this.withMicrosoft) {
          amplitude.getInstance().logEvent('Trial Creation')
          const success = await this.submitExternalTrialUser()
          if (success) {
            await this.getLoggedUserInfo()
            this.$router.push({ name: routes.feed })
          }
        } else if (!this.trialInvitation) {
          amplitude.getInstance().logEvent('Trial Creation')
          return this.submitTrialUser()
        } else {
          amplitude.getInstance().logEvent('Trial Invitation Accepted')
          return this.setAndSubmitTrialUserFromInvitation(this.$route.query.invitedUserId)
        }
      }
    },
    updateSchoolAddress(address, placeResultData) {
      const countryCode = placeResultData.address_components.find(com => com.types.includes('country')).short_name
      const {
        country,
        latitude,
        longitude,
        route,
        locality,
        // eslint-disable-next-line camelcase
        administrative_area_level_1,
        // eslint-disable-next-line camelcase
        administrative_area_level_2,
        // eslint-disable-next-line camelcase
        street_number
      } = address
      this.formatedAddress = placeResultData.adr_address.replace(/<[^>]*>?/gm, '')
      this.changeTrialSchoolValue({ field: 'country', value: country })
      this.changeTrialSchoolValue({ field: 'countryISO2', value: countryCode })
      this.changeTrialSchoolValue({ field: 'addressLatitude', value: latitude })
      this.changeTrialSchoolValue({ field: 'addressLongitude', value: longitude })
      // eslint-disable-next-line camelcase
      this.changeTrialSchoolValue({ field: 'address', value: street_number ? `${route} ${street_number}` : route })
      // eslint-disable-next-line camelcase
      this.changeTrialSchoolValue({ field: 'city', value: locality || administrative_area_level_2 || administrative_area_level_1 })
    },
    blurAddress() {
      this.$refs.schoolAddress.autocompleteText = this.formatedAddress
    },
    waitForValidation () {
      return new Promise((resolve) => {
        const unwatch = this.$watch(() => !this.$v.trialUser.$pending, (isNotPending) => {
          if (isNotPending) {
            unwatch()
            resolve(!this.$v.trialUser.$invalid)
          }
        }, { immediate: true })
      })
    },
    async verifyInvitation(inviteToken) {
      const invitedUserId = this.$route.query.invitedUserId
      await this.getUserTrialInfoByInvitation(inviteToken)
      if (invitedUserId && invitedUserId === this.trialUser.userId) {
        this.isSchoolChange = true
      }
      if (this.trialUser.schoolTrial) {
        this.trialInvitation = true
      }
    },
    setAndSubmitTrialUserFromInvitation(existingInvitedUserId) {
      const positionOption = this.positions.find(position => position.position === this.trialUser.position)
      const isSchoolChange = existingInvitedUserId !== null && existingInvitedUserId !== ''
      const trialDataInvitation = {
        ...deepCopy(baseNewUserWithoutSchool),
        password: this.trialUser.password,
        confirmPassword: this.trialUser.confirmPassword,
        inviteToken: this.$route.query.inviteToken,
        userVO: {
          userId: isSchoolChange ? existingInvitedUserId : null,
          email: this.trialUser.email,
          fullName: this.trialUser.fullName,
          cellPhoneNumber: this.trialUser.cellPhoneNumber,
          position: positionOption
        }
      }
      this.submitTrialUserInvitation(trialDataInvitation)
    }
  },
  validations() {
    const trialInvitation = {
      trialUser: {
        fullName: { required },
        email: {
          required,
          email
        },
        repeatEmail: {
          required,
          sameAsEmail: sameAs('email')
        },
        password: {
          required,
          minLength: (value) => value.length >= 6,
          mustHaveUpperAndLower: (value) => /(?=.*[a-z])(?=.*[A-Z])/.test(value),
          mustHaveANumber: (value) => /\d/.test(value)
        },
        confirmPassword: {
          required,
          sameAsPassword: sameAs('password')
        },
        school: {
          name: { required },
          address: {
            required
          }
        }
      },
      termsAndConditions: { required }
    }
    const defaultTrial = {
      trialUser: {
        fullName: { required },
        email: {
          required,
          email,
          isUnique(value) {
            return this.trialInvitation
              ? isUnique(value, usedMail, validatorAsyncFields.email)
              : true
          }
        },
        repeatEmail: {
          required,
          sameAsEmail: sameAs('email')
        },
        password: (this.withGoogle || this.withMicrosoft) ? {} : {
          required,
          minLength: (value) => value.length >= 6,
          mustHaveUpperAndLower: (value) => /(?=.*[a-z])(?=.*[A-Z])/.test(value),
          mustHaveANumber: (value) => /\d/.test(value)
        },
        confirmPassword: (this.withGoogle || this.withMicrosoft) ? {} : {
          required,
          sameAsPassword: sameAs('password')
        },
        school: {
          name: { required },
          impartedLevels: { required, minLength: minLength(1) },
          city: { required },
          country: { required },
          addressLongitude: { required },
          addressLatitude: { required },
          address: {
            required,
            unique(value) {
              return this.trialInvitation
                ? isUnique(value, usedAddress, validatorAsyncFields.address)
                : true
            }
          }
        }
      },
      termsAndConditions: { required }
    }
    return this.trialInvitation ? trialInvitation : defaultTrial
  }
}
</script>

<style lang="scss" scoped>
@import '../scss/variables/colors';
@import '../scss/variables/sizes';

.warning{
  background-color: $dark-gray;
  border-radius: 5px;
  padding: 10px;
  box-shadow: 0px 5px 5px $shadow-gray2;
}

form{
  max-width: 1024px;
  margin: 0 auto;
}

.external-buttons{
  gap: 40px;
}

.submit-button {
  --max-width: 270px;
}

.external-button{
  min-width: 300px;
  max-width: 300px;
}

.divider{
  border-bottom: 1px solid $gray;
  .o {
    right: 50%;
    background: $white;
    border: 6px solid $white;
    transform: translate(50%, -50%);
  }
}

.checkmark {
  background-color: $white;
  border: 1px solid $gray;
  border-radius: 6px;
  box-sizing: border-box;
  height: 21px;
  width: 21px;
}

.checkbox {
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkbox:checked ~ .checkmark {
  background: $blue url('../assets/ic_check-incircle.svg');
  border: none;
}

.inputs-container {
  border-bottom: 1px solid $light-gray;
  max-width: 480px;
  padding-bottom: 16px;
  width: 100%;

  .input-border {
    color: $black;

    &:focus {
      border-color: $blue;
    }
  }
}

.double-inputs-container{
  flex-wrap: wrap;
  &>*{
    flex: 1 1 35%;
    margin-bottom: 20px;
  }
  &>*:nth-child(odd){
    margin-right: 15px;
  }
  &>*:nth-child(even){
    margin-left: 15px;
  }
}

.profile-img {
  border-radius: 100%;
  height: 170px;
  width: 170px;
}

.term-error {
  border-color: $red;
}

.filled{
  background-color: $lighter-blue;
}

@media screen and (max-width: $tablet-break){
  .double-inputs-container{
    display: block;
    &>*{
      margin: 0 0 20px 0;
    }
    &>*:nth-child(odd){
      margin-right: 0;
    }
    &>*:nth-child(even){
      margin-left: 0;
    }
  }
  .submit-button{
    max-width: none;
  }
  form{
    padding: 0 25px;
  }
}

@media screen and (max-width: $mobile-break){
  .external-buttons{
    flex-direction: column;
    gap: 10px;
  }
  .external-button{
    max-width: none;
    width: 100%;
  }
  .title{
    text-align: center;
  }
}
</style>
